// Sign-in only. There is no self-service signup: accounts are created by an
// organization (a centre admin or instructor adds you), and you set your own
// password from the invite link in your email. After that, this is where you
// come back each time with your email and password.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { BRAND } from '@/lib/brand';

function Field({ id, label, type = 'text', value, onChange, placeholder, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mono-stat block">{label}</label>
      <input
        id={id}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="mt-2 w-full border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
      />
    </div>
  );
}

export default function Login() {
  const { signIn } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await api.login({ email: form.email, password: form.password });
      signIn(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto grid min-h-screen max-w-6xl lg:grid-cols-2">
        <div className="hidden flex-col justify-between border-r border-slate-200 p-12 lg:flex">
          <p className="h-display text-xl">{BRAND}<span className="text-indigo-600">.</span></p>
          <div>
            <p className="mono-stat">CELPIP · PTE Core</p>
            <h1 className="h-display mt-4 text-5xl leading-[1.04]">
              Walk into test day already sure.
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-slate-500">
              Every task type taught, every trap named, every practice set timed like the
              real exam, and every wrong answer explained.
            </p>
          </div>
          <p className="mono-stat">Access by invitation</p>
        </div>

        <div className="flex items-center p-6 lg:p-12">
          <div className="w-full max-w-sm">
            <p className="h-display mb-8 text-xl lg:hidden">{BRAND}<span className="text-indigo-600">.</span></p>
            <p className="mono-stat">Sign in</p>
            <h2 className="h-display mt-2 text-2xl">Welcome back.</h2>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <Field id="email" label="Email" type="email" value={form.email} onChange={set('email')} testid="auth-email" />
              <Field id="password" label="Password" type="password" value={form.password} onChange={set('password')} testid="auth-password" />
              {error && (
                <p className="border-l-2 border-red-500 pl-3 text-sm text-red-600" data-testid="auth-error">{error}</p>
              )}
              <button type="submit" disabled={busy} className="cta w-full justify-center disabled:opacity-50" data-testid="auth-submit">
                {busy ? 'Please wait' : 'Sign in'}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
            <p className="mt-6 text-sm text-slate-500" data-testid="auth-first-time">
              First time here? Your organization sent you an email invite. Open it and set a
              password to get in. There's no code to enter.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
